import { invalidChars } from "@/utilities/chars";

function initialState() {
    return {
        name: null,
        phone: null,
        email: null,
        emailConfirm: null,
        identificationType: null,
        controlNumbers: null,
        newsletterAccept: null,
        termsAccept: false,
        deliveryName: null,
        deliveryAddress: null,
        deliveryZip: null,
        deliveryCity: null,
        deliveryCountry: "Danmark",
        deliveryTermsAccept: false
    };
}

function validateName({ getName: value, getNameInvalidChars: invalidChars }) {
    if (!value) return ERROR_REQUIRED;
    if (invalidChars.length) return ERROR_INVALID_CHARS;
    return null;
}

function validateEmail({
    getEmail: value,
    getEmailInvalidChars: invalidChars
}) {
    if (!value) return ERROR_REQUIRED;
    if (value.indexOf("@") === -1 || value.indexOf(".") === -1)
        return ERROR_EMAIL_INVALID;
    if (invalidChars.length) return ERROR_INVALID_CHARS;
    return null;
}

function validateEmailConfirm({ getEmailConfirm: value, getEmail: other }) {
    return value !== other ? ERROR_EMAIL_MISMATCH : null;
}

function validateIdentificationType({ getIdentificationType: value }) {
    return value ? null : ERROR_REQUIRED;
}

function validateControlNumbers({
    getControlNumbers: value,
    getIdentificationType: identificationType
}) {
    if (!value) return ERROR_REQUIRED;
    if (isNaN(value)) return ERROR_CONTROL_NUMBERS_INVALID;

    if (identificationType) {
        if (
            !identificationType.useDigitsFromEnd &&
            identificationType.numberOfControlDigits !== value.length
        ) {
            return ERROR_CONTROL_NUMBERS_LENGTH_FIRST;
        }
        if (
            identificationType.useDigitsFromEnd &&
            identificationType.numberOfControlDigits !== value.length
        ) {
            return ERROR_CONTROL_NUMBERS_LENGTH_LAST;
        }
    }

    return null;
}

function validatePhone() {
    return null;
}

function validateDelivery(key) {
    const Key = key.charAt(0).toUpperCase() + key.slice(1);
    return getters => {
        const value = getters[`getDelivery${Key}`];
        const invalidChars = getters[`getDelivery${Key}InvalidChars`];
        if (!value) return ERROR_REQUIRED;
        if (invalidChars.length) return ERROR_INVALID_CHARS;
        return null;
    };
}

// this is only for checking if customer data is valid, and not if user has agreed to the terms
function isValid(_state, getters, _rootState, rootGetters) {
    const deliveryMethod = rootGetters["order/getDeliveryMethod"];

    const generic = ![
        getters.getNameError,
        getters.getPhoneError,
        getters.getEmailError,
        getters.getEmailConfirmError
    ].filter(e => Boolean(e))[0];

    let specific = true;

    if (deliveryMethod === "email") {
        specific = ![
            getters.getIdentificationTypeError,
            getters.getControlNumbersError
        ].filter(e => Boolean(e))[0];
    }

    if (deliveryMethod === "snailmail") {
        specific = ![
            getters.getDeliveryNameError,
            getters.getDeliveryAddressError,
            getters.getDeliveryZipError,
            getters.getDeliveryCityError,
            getters.getDeliveryCountryError
        ].filter(e => Boolean(e))[0];
    }

    return generic && specific;
}

const fields = [
    { key: "name", charSet: "common", validate: validateName },
    { key: "phone", validate: validatePhone },
    { key: "email", charSet: "mailAddress", validate: validateEmail },
    { key: "emailConfirm", validate: validateEmailConfirm },
    { key: "identificationType", validate: validateIdentificationType },
    { key: "controlNumbers", validate: validateControlNumbers },
    {
        key: "deliveryName",
        charSet: "common",
        validate: validateDelivery("name")
    },
    {
        key: "deliveryAddress",
        charSet: "common",
        validate: validateDelivery("address")
    },
    {
        key: "deliveryZip",
        charSet: "common",
        validate: validateDelivery("zip")
    },
    {
        key: "deliveryCity",
        charSet: "common",
        validate: validateDelivery("city")
    },
    {
        key: "deliveryCountry",
        charSet: "common",
        validate: validateDelivery("country")
    }
];

export const ERROR_REQUIRED = "error_required";
export const ERROR_INVALID_CHARS = "error_invalid_chars";
export const ERROR_EMAIL_INVALID = "error_email_invalid";
export const ERROR_EMAIL_MISMATCH = "error_email_mismatch";
export const ERROR_CONTROL_NUMBERS_INVALID = "error_control_numbers_invalid";
export const ERROR_CONTROL_NUMBERS_LENGTH_FIRST =
    "error_control_numbers_length_first";
export const ERROR_CONTROL_NUMBERS_LENGTH_LAST =
    "error_control_numbers_length_last";

export const state = initialState();

export const mutations = {
    updateCustomer: (state, payload) => {
        Object.keys(payload).forEach(key => {
            if (state.hasOwnProperty(key)) {
                state[key] = payload[key];
            }
        });
    },
    resetCustomer: state => {
        const s = initialState();
        Object.keys(s).forEach(key => {
            state[key] = s[key];
        });
    }
};

export const getters = {
    isValid,
    getNewsletterAccept: state => state.newsletterAccept,
    getTermsAccept: state => state.termsAccept,
    getDeliveryTermsAccept: state => state.deliveryTermsAccept
};

fields.forEach(({ key, charSet, validate }) => {
    const Key = key.charAt(0).toUpperCase() + key.slice(1);
    getters[`get${Key}`] = state => state[key];

    if (charSet) {
        getters[`get${Key}InvalidChars`] = (
            _state,
            getters,
            _rootState,
            rootGetters
        ) =>
            invalidChars(
                getters[`get${Key}`],
                rootGetters[`system/${charSet}CharacterSet`]
            );
    }

    if (validate) {
        getters[`get${Key}Error`] = (_state, getters) => validate(getters);
    }
});

export const actions = {};
